/**
 * useAutoSave - CR-5461
 *
 * Debounced server-side auto-save. Watches a serialisable value and calls
 * the provided save function after `delayMs` of inactivity. Returns a
 * status indicator the UI can surface to reassure the user their work is
 * persisted.
 *
 * Designed for the RFQ wizard but generic enough to reuse for any future
 * server-backed draft flow.
 *
 * @param value         The state to persist. Changes trigger the timer.
 * @param save          Async function that persists `value` server-side.
 * @param delayMs       Debounce delay in ms (default 3000).
 * @param enabled       When false, skips all saves. Useful while showing a
 *                      "resume draft?" prompt or before the user has
 *                      meaningfully interacted with the form.
 * @returns { status, lastSavedAt, flush } — status is one of:
 *          'idle'   - no save attempted yet this session
 *          'saving' - in-flight save
 *          'saved'  - last save succeeded
 *          'error'  - last save failed
 *          flush() (CR-6207) saves the latest value straight away instead of
 *          waiting out the debounce — for a host that is about to close or
 *          unmount. It only saves when a save is still owed (one is scheduled,
 *          or the last attempt failed) and the hook is enabled; otherwise it
 *          waits for any save already in flight. Resolves true when nothing
 *          is owed or the save landed, false when it failed. Never rejects.
 *
 * @example
 * const { status, flush } = useAutoSave(form, saveForm, 3000, isDirty);
 * const close = async () => { if (await flush()) onClose(); };
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function useAutoSave<T>(
  value: T,
  save: (value: T) => Promise<void>,
  delayMs: number = 3000,
  enabled: boolean = true,
): { status: AutoSaveStatus; lastSavedAt: Date | null; flush: () => Promise<boolean> } {
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;
  const valueRef = useRef(value);
  valueRef.current = value;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // CR-6207: true while the latest value has not reached the server — set
  // when a save is scheduled, cleared when one starts, set again when it
  // fails. Only flush() reads it.
  const owedRef = useRef(false);
  const inFlightRef = useRef<Promise<boolean> | null>(null);

  const run = useCallback((): Promise<boolean> => {
    owedRef.current = false;
    setStatus('saving');
    const attempt = (async () => {
      try {
        await saveRef.current(valueRef.current);
        setStatus('saved');
        setLastSavedAt(new Date());
        return true;
      } catch {
        // Network/server error — surface as 'error' but don't toast on
        // every tick (auto-save failure shouldn't spam the user). The
        // UI shows a status pill instead.
        owedRef.current = true;
        setStatus('error');
        return false;
      }
    })();
    inFlightRef.current = attempt;
    void attempt.then(() => {
      if (inFlightRef.current === attempt) inFlightRef.current = null;
    });
    return attempt;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    owedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void run();
    }, delayMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // value is intentionally the only dep that triggers re-run. save/delayMs/enabled
    // changes via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enabled]);

  const flush = useCallback(async (): Promise<boolean> => {
    if (enabledRef.current && owedRef.current) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return run();
    }
    return inFlightRef.current ?? true;
  }, [run]);

  return { status, lastSavedAt, flush };
}
